import { useState, useEffect, Fragment, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { RefreshCw, Loader2, CheckCircle2, XCircle, MinusCircle, AlertTriangle, ChevronRight } from 'lucide-react'

type Status = 'ok' | 'expected' | 'skipped' | 'warn' | 'error' | 'critical'
interface Dataset {
  dataset_key: string; label: string; provider: string | null; universe: string | null
  first_date: string | null; last_date: string | null; rows: number | null
  staleness_days: number | null; status: Status; expandable?: boolean
}
interface Job { key: string; label: string; last_refresh: { status: string; message: string | null; at: string | null } | null }
interface Group { key: string; label: string; description?: string | null; jobs: Job[]; datasets: Dataset[] }
interface Explorer { snapshot_run_id: string | null; snapshot_at: string | null; groups: Group[] }

const fmtDate = (s: string | null) => {
  if (!s) return '—'
  const d = new Date(s + 'T00:00:00')
  return isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
}
const fmtNum = (n: number | null) => (n == null ? '—' : n.toLocaleString())

function upToDate(s: Status) {
  switch (s) {
    case 'ok': return { chip: 'bg-green-500/10 text-green-700', dot: 'bg-green-500', label: 'Up to date' }
    case 'expected': return { chip: 'bg-slate-500/10 text-slate-600', dot: 'bg-slate-400', label: 'Frozen' }
    case 'warn': return { chip: 'bg-amber-500/10 text-amber-700', dot: 'bg-amber-500', label: 'Stale' }
    case 'critical': case 'error': return { chip: 'bg-red-600/10 text-red-700', dot: 'bg-red-600', label: 'Down' }
    default: return { chip: 'bg-slate-400/10 text-slate-500', dot: 'bg-slate-300', label: s }
  }
}

const TERMINAL = ['success', 'failed', 'skipped']

export default function DataExplorer() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<Explorer>({
    queryKey: ['data-explorer'],
    queryFn: () => api.get('/data/explorer').then((r) => r.data),
  })
  // active app-triggered refreshes, keyed by job_key
  const [active, setActive] = useState<Record<string, { id: number; status: string; message?: string }>>({})
  // which datasets are expanded to their per-member breakdown, keyed by dataset_key
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const toggle = (k: string) => setExpanded((e) => ({ ...e, [k]: !e[k] }))

  useEffect(() => {
    const running = Object.entries(active).filter(([, v]) => v.id > 0 && !TERMINAL.includes(v.status))
    if (running.length === 0) return
    const t = setInterval(async () => {
      for (const [job, v] of running) {
        try {
          const r = await api.get(`/data/explorer/refresh/${v.id}`).then((x) => x.data)
          setActive((a) => ({ ...a, [job]: { id: v.id, status: r.status, message: r.message } }))
          if (TERMINAL.includes(r.status)) qc.invalidateQueries({ queryKey: ['data-explorer'] })
        } catch { /* keep polling */ }
      }
    }, 2500)
    return () => clearInterval(t)
  }, [active, qc])

  const startRefresh = async (jobKey: string) => {
    setActive((a) => ({ ...a, [jobKey]: { id: 0, status: 'queued' } }))
    try {
      const r = await api.post('/data/explorer/refresh', { job_key: jobKey }).then((x) => x.data)
      setActive((a) => ({ ...a, [jobKey]: { id: r.refresh_id, status: 'queued' } }))
    } catch (e: any) {
      setActive((a) => ({ ...a, [jobKey]: { id: 0, status: 'failed', message: e?.response?.data?.detail || 'failed to queue' } }))
    }
  }

  if (isLoading) return <div className="flex justify-center p-8"><span className="spinner" /></div>

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Data · Explorer</h1>
          <p className="text-muted-foreground">
            Every provider and dataset, its date range and row count, and whether it is up to date. Launch a refresh and monitor it live.
          </p>
        </div>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['data-explorer'] })}
          className="h-9 px-3 border border-border rounded-md text-sm hover:bg-muted flex items-center gap-1.5">
          <RefreshCw className="h-4 w-4" /> Reload
        </button>
      </div>
      {data?.snapshot_at && (
        <div className="text-xs text-muted-foreground -mt-3">
          Catalog snapshot: {new Date(data.snapshot_at).toLocaleString()}
        </div>
      )}

      {data?.groups.map((g) => (
        <div key={g.key} className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <h2 className="font-semibold">{g.label}</h2>
                <span className="text-xs text-muted-foreground">
                  {g.datasets.length} {g.key === 'portfolio_funds' ? 'funds' : 'datasets'}
                </span>
              </div>
              {g.description && (
                <p className="text-xs text-muted-foreground mt-0.5 max-w-3xl">{g.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {g.jobs.length === 0 && (
                <span className="text-xs text-muted-foreground italic">upstream / nightly — no on-demand refresh</span>
              )}
              {g.jobs.map((job) => {
                const a = active[job.key]
                const busy = a && !TERMINAL.includes(a.status)
                return (
                  <div key={job.key} className="flex items-center gap-2">
                    {a && (
                      <RefreshBadge status={busy ? 'running' : a.status} message={a.message} />
                    )}
                    {!a && job.last_refresh && (
                      <span className="text-xs text-muted-foreground" title={job.last_refresh.message || ''}>
                        last: {job.last_refresh.status}
                      </span>
                    )}
                    <button onClick={() => startRefresh(job.key)} disabled={busy}
                      className="h-8 px-3 rounded-md text-xs font-medium border border-input hover:bg-accent
                                 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      Refresh {job.label}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{g.key === 'portfolio_funds' ? 'Fund' : 'Dataset'}</th>
                  <th>{g.key === 'portfolio_funds' ? 'Weight' : 'Scope'}</th>
                  <th>First date</th><th>Last date</th>
                  <th className="text-right">{g.key === 'portfolio_funds' ? 'Months' : 'Rows'}</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {g.datasets.map((d) => {
                  const st = upToDate(d.status)
                  const isOpen = !!expanded[d.dataset_key]
                  return (
                    <Fragment key={d.dataset_key}>
                      <tr
                        className={d.expandable ? 'cursor-pointer hover:bg-muted/40' : ''}
                        onClick={d.expandable ? () => toggle(d.dataset_key) : undefined}
                      >
                        <td className="font-medium">
                          <div className="flex items-center gap-1.5">
                            {d.expandable
                              ? <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                              : <span className="inline-block w-3.5" />}
                            <span>{d.label}</span>
                          </div>
                          <div className="text-xs text-muted-foreground pl-5">{d.provider}</div>
                        </td>
                        <td className="text-sm text-muted-foreground">{d.universe}</td>
                        <td className="text-sm">{fmtDate(d.first_date)}</td>
                        <td className="text-sm">{fmtDate(d.last_date)}</td>
                        <td className="text-right font-mono text-sm">{fmtNum(d.rows)}</td>
                        <td>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.chip}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
                          </span>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={6} className="bg-muted/20 p-0">
                            <MemberDrilldown datasetKey={d.dataset_key} label={d.label} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}

function MemberDrilldown({ datasetKey, label }: { datasetKey: string; label: string }) {
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [behindOnly, setBehindOnly] = useState(false)
  const [kindFilter, setKindFilter] = useState<'all' | 'Fund' | 'Index'>('all')
  // debounce the search so we don't fire a query per keystroke
  useEffect(() => {
    const t = setTimeout(() => setQ(search), 350)
    return () => clearTimeout(t)
  }, [search])
  const { data, isLoading, isError } = useQuery({
    queryKey: ['explorer-members', datasetKey, q],
    queryFn: () => api.get(`/data/explorer/members/${datasetKey}?q=${encodeURIComponent(q)}&limit=500`).then((r) => r.data),
  })

  const members: any[] = data?.members ?? []
  const behind = members.filter((m) => m.status && m.status !== 'ok') // needs updating
  // Fund vs Index split (only present for hedge-fund datasets)
  const hasKind = members.some((m) => m.kind)
  const nFund = members.filter((m) => m.kind === 'Fund').length
  const nIndex = members.filter((m) => m.kind === 'Index').length
  let rows = behindOnly ? behind : members
  if (kindFilter !== 'all') rows = rows.filter((m) => m.kind === kindFilter)

  return (
    <div className="p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${label}…`}
          className="h-8 flex-1 min-w-[180px] max-w-sm px-3 border border-input rounded-md bg-background text-sm"
        />
        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" checked={behindOnly} onChange={(e) => setBehindOnly(e.target.checked)} className="rounded border-input" />
          Behind only
        </label>
        {hasKind && (
          <div className="inline-flex items-center gap-1">
            {([['all', 'All'], ['Fund', `Funds (${nFund})`], ['Index', `Indices (${nIndex})`]] as const).map(([k, lbl]) => (
              <button
                key={k}
                onClick={() => setKindFilter(k)}
                className={`px-2.5 py-1 rounded-md text-xs border ${kindFilter === k ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-accent'}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="text-xs text-muted-foreground py-2 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading members…</div>
      ) : isError ? (
        <div className="text-xs text-red-600 py-2">Could not load members — try a narrower search.</div>
      ) : !members.length ? (
        <div className="text-xs text-muted-foreground py-2">No members match.</div>
      ) : (
        <>
          <div className="text-xs text-muted-foreground">
            <span className="text-amber-600 dark:text-amber-400 font-medium">{behind.length} behind</span>
            {' '}of {data.count}{data.capped ? '+' : ''}{data.capped ? ' (capped — refine search to see all)' : ''} · oldest first
          </div>
          <div className="max-h-80 overflow-y-auto border border-border rounded-md">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card">
                <tr className="text-muted-foreground">
                  <th className="text-left px-3 py-1.5 font-medium">Member</th>
                  {hasKind && <th className="text-left px-3 py-1.5 font-medium">Type</th>}
                  <th className="text-left px-3 py-1.5 font-medium">First</th>
                  <th className="text-left px-3 py-1.5 font-medium">Last</th>
                  <th className="text-right px-3 py-1.5 font-medium">Rows</th>
                  <th className="text-left px-3 py-1.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={hasKind ? 6 : 5} className="px-3 py-4 text-center text-muted-foreground">Nothing to show for this filter.</td></tr>
                ) : rows.map((m: any, i: number) => {
                  const st = upToDate(m.status)
                  return (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-1 font-medium">{m.member}</td>
                      {hasKind && (
                        <td className="px-3 py-1">
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${m.kind === 'Index' ? 'bg-slate-500/12 text-slate-500 dark:text-slate-300' : 'bg-primary/10 text-primary'}`}>{m.kind}</span>
                        </td>
                      )}
                      <td className="px-3 py-1">{fmtDate(m.first_date)}</td>
                      <td className="px-3 py-1">{fmtDate(m.last_date)}</td>
                      <td className="px-3 py-1 text-right font-mono">{fmtNum(m.rows)}</td>
                      <td className="px-3 py-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${st.chip}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function RefreshBadge({ status, message }: { status: string; message?: string }) {
  const map: Record<string, { icon: ReactNode; cls: string; label: string }> = {
    running: { icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />, cls: 'text-blue-600', label: 'Running…' },
    queued: { icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />, cls: 'text-blue-600', label: 'Queued…' },
    success: { icon: <CheckCircle2 className="h-3.5 w-3.5" />, cls: 'text-green-600', label: 'Done' },
    skipped: { icon: <MinusCircle className="h-3.5 w-3.5" />, cls: 'text-slate-500', label: 'Skipped' },
    failed: { icon: <XCircle className="h-3.5 w-3.5" />, cls: 'text-red-600', label: 'Failed' },
  }
  const m = map[status] || { icon: <AlertTriangle className="h-3.5 w-3.5" />, cls: 'text-amber-600', label: status }
  return <span className={`inline-flex items-center gap-1 text-xs font-medium ${m.cls}`} title={message || ''}>{m.icon}{m.label}</span>
}
